<template>
  <div class="asset-upload-button">
    <el-button
      size="small"
      type="primary"
      icon="el-icon-upload2"
      :loading="uploading"
      @click="triggerSelect"
    >
      {{ uploading ? `上传中 ${processedFiles + 1}/${totalFiles}` : '上传素材' }}
    </el-button>
    <!-- 2026-10-09 用户要求：音频也支持多选上传（此前只有图片可多选） -->
    <span v-if="!uploading" class="aub-hint">可多选</span>
    <input
      ref="fileInput"
      type="file"
      class="aub-native-input"
      :accept="acceptAttr"
      :multiple="true"
      :disabled="uploading"
      @change="onInputChange"
    >
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import type { AssetKind, AssetScope, Asset } from '@/api/asset'
import { uploadAsset } from '@/api/asset'
import { validateAssetFile, readAudioDuration, readImageSize, isAudioKind, ASSET_LIMITS } from '@/utils/assetUpload'

@Component
export default class AssetUploadButton extends Vue {
  @Prop({ required: true }) readonly kind!: AssetKind
  // 必须显式传值：AssetLibrary 传当前 scope（official/mine/book），组件内部按
  // scope === 'official' 换算出上传接口需要的 official 布尔；不传时后端会按角色兜底，
  // admin 在个人库上传会被误落进官方库（全体作者可见），所以这里不给默认值，逼调用方显式决定。
  @Prop({ required: true }) readonly scope!: AssetScope
  /** 本书 id：scope==='book' 时 AssetLibrary 传真实 game_id，其余 scope 传 0。
      2026-09-18 本书素材库改造起 book 是可写库，上传要带 game_id 让后端解析归属并做书级鉴权 */
  @Prop({ default: 0 }) readonly gameId!: number
  /** 上传落到哪个分组；null = 未分类（选中「全部」/「未分类」时）。由 AssetLibrary 按分类树的
      选中值算好传进来（utils/assetCategoryHierarchy 的 uploadTarget） */
  @Prop({ default: null }) readonly targetCategoryId!: number | null
  /** 非空表示当前选中的分组有子分组、不能往里传：点上传只弹这句话，不打开文件选择框。
      文案由 AssetLibrary 组装（它知道分组名），这里只负责展示 */
  @Prop({ default: '' }) readonly blockedReason!: string

  uploading = false
  totalFiles = 0
  processedFiles = 0

  // 原生 input 的 accept 提示，随 kind 变化列出对应扩展名；只是减少选错文件的概率，
  // 真正把关的是下面的 validateAssetFile（预检）和后端（内容嗅探）
  get acceptAttr(): string {
    return ASSET_LIMITS[this.kind].exts.map(ext => '.' + ext).join(',')
  }

  triggerSelect() {
    if (this.uploading) return
    // 选中的是有子分组的分组时，在打开文件选择框**之前**就拦住：让作者先挑好位置，
    // 而不是选完文件才被拒。后端另有权威校验，这里只是体验（素材只能挂叶子分组）
    if (this.blockedReason) {
      this.$message.warning(this.blockedReason)
      return
    }
    const input = this.$refs.fileInput as HTMLInputElement
    input.click()
  }

  onInputChange(e: Event) {
    const input = e.target as HTMLInputElement
    const files = Array.from(input.files || [])
    input.value = '' // 清空，确保之后重新选中同一个文件也能再次触发 change
    if (files.length) this.handleFiles(files)
  }

  // 整批共享选择时的类型、库、书籍和分组；中途切页签不能改变后续文件的归属。
  // 逐张预检 → 读信息 → 上传；失败只影响当前文件，批次结束后统一刷新和提示。
  async handleFiles(files: File[]) {
    if (this.uploading || !files.length) return
    if (this.blockedReason) {
      this.$message.warning(this.blockedReason)
      return
    }
    const kind = this.kind
    const official = this.scope === 'official'
    const categoryId = this.targetCategoryId
    const gameId = this.gameId
    const failures: { name: string; reason: string }[] = []
    let lastUploaded: Asset | undefined
    let succeeded = 0
    this.totalFiles = files.length
    this.processedFiles = 0
    this.uploading = true
    try {
      for (const file of files) {
        try {
          const validationError = validateAssetFile(file, kind)
          if (validationError) throw new Error(validationError)

          let durationSec: number | undefined
          let width: number | undefined
          let height: number | undefined
          if (isAudioKind(kind)) {
            durationSec = await readAudioDuration(file)
          } else {
            const size = await readImageSize(file)
            width = size.width
            height = size.height
          }

          const res = await uploadAsset({
            file,
            kind,
            official,
            durationSec,
            width,
            height,
            categoryId,
            gameId: gameId > 0 ? gameId : null
          }, { _silentError: true })
          // HTTP 200 也可能是业务失败，不能计入成功数量。
          if (res.data.code !== 0) throw new Error(res.data.message || '上传失败')
          lastUploaded = res.data.data as Asset
          succeeded++
        } catch (e) {
          failures.push({ name: file.name, reason: this.uploadErrorMessage(e) })
        } finally {
          this.processedFiles++
        }
      }
    } finally {
      this.uploading = false
    }

    // 父组件只需刷新一次，避免批量上传触发大量重复的列表请求。
    if (lastUploaded) this.$emit('uploaded', lastUploaded)
    // 音频也能多选了（2026-10-09 用户要求），成功/失败文案按 kind 区分量词
    const unit = isAudioKind(kind) ? '个音频' : '张图片'
    if (!failures.length) {
      this.$message.success(files.length === 1 ? '上传成功' : `上传成功，共 ${succeeded} ${unit}`)
    } else if (files.length === 1) {
      this.$message.error(failures[0].reason)
    } else {
      const h = this.$createElement
      const message = h('div', [
        h('p', `成功 ${succeeded} ${unit}，失败 ${failures.length} ${unit}。可重新选择失败的文件上传。`),
        h('ul', { style: { maxHeight: '300px', overflowY: 'auto', paddingLeft: '20px', overflowWrap: 'anywhere' } },
          failures.map(f => h('li', `${f.name}：${f.reason}`)))
      ])
      this.$msgbox({
        title: '上传结果', message, type: 'warning', confirmButtonText: '知道了', closeOnClickModal: false
      }).catch(() => { /* 关闭结果提示 */ })
    }
  }

  uploadErrorMessage(error: unknown): string {
    const e = error as { message?: string; response?: { status?: number; data?: { message?: string; error?: string } | string } }
    if (e?.response?.status === 413) return '文件过大，超出服务器允许的上传大小'
    const data = e?.response?.data
    if (data && typeof data === 'object') return data.message || data.error || '上传失败'
    if (typeof data === 'string' && data.trim() && !/^\s*</.test(data)) return data
    return e?.message || '上传失败，请稍后重试'
  }
}
</script>

<style scoped lang="scss">
.asset-upload-button {
  display: inline-block;
}

.aub-native-input {
  display: none;
}

.aub-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}
</style>
